import { z } from "zod";

export const FieldWidgetSchema = z.enum([
  "shortText", "longText", "number", "email", "phone", "date",
  "radio", "dropdown", "multipleChoice", "yesNo", "signature",
]);

const FieldIdSchema = z.string().regex(/^field_[a-zA-Z0-9_-]+$/);
const SectionIdSchema = z.string().regex(/^section_[a-zA-Z0-9_-]+$/);
const ChoiceSchema = z.strictObject({
  value: z.string().regex(/^choice_[a-zA-Z0-9_-]+$/),
  label: z.string(),
});

export const BuilderFieldSchema = z.strictObject({
  id: FieldIdSchema,
  widget: FieldWidgetSchema,
  label: z.string(),
  helpText: z.string(),
  required: z.boolean(),
  choices: z.array(ChoiceSchema),
});

const FieldElementSchema = z.strictObject({
  type: z.literal("field"),
  fieldId: FieldIdSchema,
});
const SectionElementSchema = z.strictObject({
  type: z.literal("section"),
  id: SectionIdSchema,
  title: z.string(),
  description: z.string(),
  elements: z.array(FieldElementSchema),
});
export const BuilderElementSchema = z.discriminatedUnion("type", [FieldElementSchema, SectionElementSchema]);
export const BuilderDraftSchema = z.strictObject({
  fields: z.record(FieldIdSchema, BuilderFieldSchema),
  elements: z.array(BuilderElementSchema),
});

const SchemaChoiceSchema = z.strictObject({ const: ChoiceSchema.shape.value, title: z.string() });
const PropertySchema = z.strictObject({
  type: z.enum(["string", "number", "boolean", "array"]),
  title: z.string(),
  description: z.string(),
  format: z.enum(["email", "date"]).optional(),
  minLength: z.literal(1).optional(),
  minItems: z.literal(1).optional(),
  uniqueItems: z.literal(true).optional(),
  oneOf: z.array(SchemaChoiceSchema).min(1).optional(),
  items: z.strictObject({
    type: z.literal("string"),
    oneOf: z.array(SchemaChoiceSchema).min(1).optional(),
  }).optional(),
  contentEncoding: z.literal("base64").optional(),
  contentMediaType: z.literal("image/png").optional(),
});
const UiFieldSchema = FieldElementSchema.extend({ widget: FieldWidgetSchema });
const UiSectionSchema = SectionElementSchema.extend({ elements: z.array(UiFieldSchema) });
export const FormDefinitionSchema = z.strictObject({
  format: z.literal("froppio-form"),
  formatVersion: z.literal(1),
  dataSchema: z.strictObject({
    $schema: z.literal("https://json-schema.org/draft/2020-12/schema"),
    type: z.literal("object"),
    properties: z.record(FieldIdSchema, PropertySchema),
    required: z.array(FieldIdSchema),
    additionalProperties: z.literal(false),
  }),
  uiSchema: z.strictObject({
    elements: z.array(z.discriminatedUnion("type", [UiFieldSchema, UiSectionSchema])),
  }),
});

export type FieldWidget = z.infer<typeof FieldWidgetSchema>;
export type BuilderField = z.infer<typeof BuilderFieldSchema>;
export type BuilderElement = z.infer<typeof BuilderElementSchema>;
export type FieldElement = z.infer<typeof FieldElementSchema>;
export type SectionElement = z.infer<typeof SectionElementSchema>;
export type BuilderDraft = z.infer<typeof BuilderDraftSchema>;
export type FormDefinition = z.infer<typeof FormDefinitionSchema>;
export type FieldProperty = z.infer<typeof PropertySchema>;
export type FormAnswer = string | boolean | string[];
export type FormAnswers = Record<string, FormAnswer>;
export interface BuilderWarning { id: string; message: string }
export type DefinitionResult = { success: true; draft: BuilderDraft } | { success: false; message: string };
